import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Megaphone, Plus, Send, Trash2, Instagram, Facebook, Youtube, Twitter, Linkedin, Music2, Newspaper, Tv as TvIcon, Calendar, Target, Euro, Check, X } from "lucide-react";

const OBJECTIVE_LABELS = {
  brand_awareness: "Brand awareness",
  lead_generation: "Generazione lead",
  product_launch: "Lancio prodotto",
  horeca_acquisition: "Acquisizione HoReCa",
  export_b2b: "Export B2B",
  ecommerce_sales: "Vendite e-commerce",
};

const STATUS_META = {
  draft: { label: "Bozza", cls: "bg-muted text-muted-foreground border-border" },
  submitted: { label: "Inviata", cls: "bg-blue-500/15 text-blue-500 border-blue-500/30" },
  approved: { label: "Approvata", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  active: { label: "In corso", cls: "bg-[#C9A961]/15 text-[#C9A961] border-[#C9A961]/40" },
  completed: { label: "Conclusa", cls: "bg-purple-500/15 text-purple-400 border-purple-500/30" },
  rejected: { label: "Rifiutata", cls: "bg-red-500/15 text-red-500 border-red-500/30" },
};

const ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, tiktok: Music2, x: Twitter, linkedin: Linkedin, newspaper: Newspaper, tv: TvIcon, music: Music2, twitter: Twitter };

const fmtEur = (n) => `€${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

function ChannelChip({ ch, channelsCatalog }) {
  const list = channelsCatalog?.[ch.type] || [];
  const meta = list.find((c) => c.id === ch.channel_id);
  const Icon = ICONS[meta?.icon || ch.channel_id] || Megaphone;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-[hsl(var(--border))] rounded-sm">
      <Icon className="h-3 w-3" />
      <span>{meta?.label || ch.channel_id}</span>
      {ch.budget > 0 && <span className="text-[#C9A961] font-semibold">· {fmtEur(ch.budget)}</span>}
    </span>
  );
}

function CreateForm({ channelsCatalog, onCreated, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title: "",
    product_name: "",
    objective: "brand_awareness",
    target_audience: "",
    total_budget: 5000,
    start_date: today,
    end_date: in30,
    creative_brief: "",
  });
  const [selected, setSelected] = useState({}); // {channel_id: {type, budget, notes}}
  const [submitting, setSubmitting] = useState(false);

  const toggleChannel = (type, channel_id) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[channel_id]) delete next[channel_id];
      else next[channel_id] = { type, channel_id, budget: 0, notes: "" };
      return next;
    });
  };

  const updateChannelBudget = (channel_id, budget) => {
    setSelected((prev) => ({ ...prev, [channel_id]: { ...prev[channel_id], budget: Number(budget) || 0 } }));
  };

  const submit = async (asDraft) => {
    if (!form.title.trim() || !form.product_name.trim()) { toast.error("Compila titolo e prodotto"); return; }
    if (Object.keys(selected).length === 0) { toast.error("Seleziona almeno un canale"); return; }
    setSubmitting(true);
    try {
      await api.post("/marketing/campaigns", {
        ...form,
        total_budget: Number(form.total_budget),
        channels: Object.values(selected),
        submit_for_approval: !asDraft,
      });
      toast.success(asDraft ? "Bozza salvata" : "Campagna inviata per approvazione");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="campaign-form" className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-6 space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Titolo campagna *</label>
          <Input data-testid="cmp-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Lancio Brunello Riserva 2018" className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Prodotto *</label>
          <Input data-testid="cmp-product" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Brunello di Montalcino Riserva 2018" className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Obiettivo</label>
          <Select value={form.objective} onValueChange={(v) => setForm({ ...form, objective: v })}>
            <SelectTrigger data-testid="cmp-objective" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(OBJECTIVE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Budget totale (€)</label>
          <Input data-testid="cmp-budget" type="number" min="0" value={form.total_budget} onChange={(e) => setForm({ ...form, total_budget: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Data inizio</label>
          <Input data-testid="cmp-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Data fine</label>
          <Input data-testid="cmp-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider font-semibold">Target / pubblico</label>
        <Input data-testid="cmp-target" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} placeholder="Es. HoReCa Lombardia, ristoratori 35-55, importatori UK" className="mt-1" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider font-semibold">Brief creativo</label>
        <Textarea data-testid="cmp-brief" rows={3} value={form.creative_brief} onChange={(e) => setForm({ ...form, creative_brief: e.target.value })} placeholder="Stile, claim, messaggi chiave, ispirazioni..." className="mt-1" />
      </div>

      {/* CHANNEL PICKER */}
      <div className="border-t border-[hsl(var(--border))] pt-5">
        <div className="text-xs uppercase tracking-wider font-semibold mb-3 flex items-center gap-2"><Target className="h-3.5 w-3.5" />Canali · seleziona uno o più</div>

        {[
          { key: "social", label: "Social Media", icon: Instagram },
          { key: "print", label: "Riviste di settore", icon: Newspaper },
          { key: "tv", label: "TV · Banner pubblicitari", icon: TvIcon },
        ].map((group) => (
          <div key={group.key} className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--accent))] flex items-center gap-2 mb-2">
              <group.icon className="h-3 w-3" />{group.label}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(channelsCatalog[group.key] || []).map((c) => {
                const Icon = ICONS[c.icon] || Megaphone;
                const on = !!selected[c.id];
                return (
                  <div key={c.id} className={`border rounded-sm p-3 transition-colors ${on ? "bg-[hsl(var(--primary))]/8 border-[hsl(var(--primary))]" : "border-[hsl(var(--border))]"}`}>
                    <button type="button" data-testid={`cmp-ch-${c.id}`} onClick={() => toggleChannel(group.key, c.id)} className="flex items-center gap-2 w-full text-left text-sm">
                      <span className={`h-4 w-4 rounded border flex items-center justify-center ${on ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))]" : "border-[hsl(var(--border))]"}`}>
                        {on && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <Icon className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                      <span className="font-medium">{c.label}</span>
                    </button>
                    {on && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground uppercase">Budget €</span>
                        <Input data-testid={`cmp-ch-budget-${c.id}`} type="number" min="0" value={selected[c.id].budget} onChange={(e) => updateChannelBudget(c.id, e.target.value)} className="h-7 text-xs" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 pt-3 border-t border-[hsl(var(--border))]">
        <Button data-testid="cmp-submit" onClick={() => submit(false)} disabled={submitting} className="bg-[hsl(var(--primary))] gap-2">
          <Send className="h-4 w-4" />Invia per approvazione
        </Button>
        <Button data-testid="cmp-draft" variant="outline" onClick={() => submit(true)} disabled={submitting}>Salva bozza</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </div>
  );
}

function CampaignCard({ c, channelsCatalog, onSubmit, onDelete }) {
  const meta = STATUS_META[c.status] || STATUS_META.draft;
  return (
    <div data-testid={`campaign-${c.id}`} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-5 hover:border-[hsl(var(--primary))]/40 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))]">{OBJECTIVE_LABELS[c.objective] || c.objective}</div>
          <h3 className="editorial-heading text-xl mt-1">{c.title}</h3>
          <div className="text-sm text-muted-foreground mt-0.5">Prodotto: <span className="text-foreground font-semibold">{c.product_name}</span></div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm font-bold ${meta.cls}`}>{meta.label}</span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {(c.channels || []).map((ch, i) => <ChannelChip key={i} ch={ch} channelsCatalog={channelsCatalog} />)}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-xs border-t border-[hsl(var(--border))] pt-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground"><Euro className="inline h-3 w-3" /> Budget</div>
          <div className="text-sm font-bold text-[#C9A961]">{fmtEur(c.total_budget)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground"><Calendar className="inline h-3 w-3" /> Periodo</div>
          <div className="text-xs">{c.start_date} → {c.end_date}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Target</div>
          <div className="text-xs truncate" title={c.target_audience}>{c.target_audience || "—"}</div>
        </div>
      </div>

      {c.admin_notes && (
        <div className="mt-3 p-3 bg-[hsl(var(--muted))] text-xs italic text-muted-foreground rounded-sm border-l-2 border-[#C9A961]">
          <strong className="text-[#C9A961] not-italic uppercase tracking-wider text-[10px]">Note admin: </strong>{c.admin_notes}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        {c.status === "draft" && (
          <Button data-testid={`cmp-${c.id}-submit`} size="sm" onClick={() => onSubmit(c.id)} className="bg-[hsl(var(--primary))] gap-2"><Send className="h-3 w-3" />Invia per approvazione</Button>
        )}
        {(c.status === "draft" || c.status === "rejected") && (
          <Button data-testid={`cmp-${c.id}-delete`} size="sm" variant="ghost" onClick={() => onDelete(c.id, c.title)} className="text-destructive gap-2"><Trash2 className="h-3 w-3" />Elimina</Button>
        )}
      </div>
    </div>
  );
}

export default function B2BMarketing() {
  const [campaigns, setCampaigns] = useState([]);
  const [creating, setCreating] = useState(false);
  const [channelsCatalog, setChannelsCatalog] = useState({});

  const reload = () => api.get("/marketing/campaigns").then((r) => setCampaigns(r.data.campaigns || []));

  useEffect(() => {
    api.get("/marketing/channels").then((r) => setChannelsCatalog(r.data.channels || {}));
    reload();
  }, []);

  const submit = async (id) => {
    try { await api.post(`/marketing/campaigns/${id}/submit`); toast.success("Inviata"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (id, title) => {
    if (!window.confirm(`Eliminare "${title}"?`)) return;
    try { await api.delete(`/marketing/campaigns/${id}`); toast.success("Eliminata"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div data-testid="b2b-marketing-page" className="container-xl py-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))] flex items-center gap-2 mb-2"><Megaphone className="h-3 w-3" />B2B · Marketing</div>
          <h1 className="editorial-heading text-4xl md:text-5xl text-[hsl(var(--foreground))]">Le tue campagne</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">Crea piani marketing su social, riviste di settore e TV. Ogni campagna passa per l'approvazione dello staff Amazonico 3000.</p>
        </div>
        {!creating && (
          <Button data-testid="cmp-new-btn" onClick={() => setCreating(true)} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 gap-2">
            <Plus className="h-4 w-4" />Nuova campagna
          </Button>
        )}
      </div>

      {creating && (
        <div className="mt-6">
          <CreateForm channelsCatalog={channelsCatalog} onCreated={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />
        </div>
      )}

      <div className="mt-8 space-y-4">
        {campaigns.length === 0 && !creating ? (
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm p-12 text-center">
            <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <div className="text-lg font-semibold">Nessuna campagna ancora</div>
            <div className="text-sm text-muted-foreground mt-1">Crea la prima campagna marketing per i tuoi prodotti.</div>
          </div>
        ) : (
          campaigns.map((c) => <CampaignCard key={c.id} c={c} channelsCatalog={channelsCatalog} onSubmit={submit} onDelete={del} />)
        )}
      </div>
    </div>
  );
}
